import { Injectable } from '@angular/core';
import {
	CachePolicy,
	CachePolicyEntity,
	camelToSnake,
	UrlService,
} from '@datachain/ui-sdk/common';
import { Observable } from 'rxjs';

import { DcBaseRequester } from '../core/dc-base.requester';
import { IDashboard, IDashboardResponse } from './dashboard.model';

@Injectable({
	providedIn: 'root',
})
export class DashboardRequester extends DcBaseRequester<IDashboardResponse> {
	protected resource;
	protected elementsListUrl;
	protected singleElementUrl;

	public constructor(private readonly urlService: UrlService) {
		super();
		this.resource = this.generateResourceUrl('DASHBOARDS_GET_ALL');
		this.elementsListUrl = this.resource;
		this.singleElementUrl = this.resource;
	}

	public getDashboardList(
		includePublic: boolean,
		useCache = true
	): Observable<ReadonlyArray<IDashboardResponse>> {
		const resourceUrl = this.generateResourceUrl('DASHBOARDS_ALL_DETAILED');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{},
			{
				project: this.projectId,
				excludePublic: !includePublic,
			}
		);
		const cachePolicy = useCache
			? CachePolicyEntity.build({
					policy: CachePolicy.NetworkAndCache,
					evictTimeoutInSeconds: 60,
					isCacheable: true,
			  })
			: CachePolicyEntity.build({
					policy: CachePolicy.NetworkOnly,
					evictTimeoutInSeconds: 0,
					isCacheable: false,
			  });
		return this.get<Array<IDashboardResponse>>(url, cachePolicy);
	}

	public getDashboard(dashboardId: number): Observable<IDashboard> {
		const resourceUrl = this.generateResourceUrl('DASHBOARDS_GET');

		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: dashboardId,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.get<IDashboard>(url);
	}

	public changeActivationStatusDashboard(
		dashboard: IDashboard
	): Observable<void> {
		const resourceUrl = this.generateResourceUrl('DASHBOARDS_EDIT');
		const url = this.urlService.generateUrl(
			resourceUrl,
			{
				id: dashboard.id,
			},
			{
				project: this.projectId,
			}
		);
		return this.http.put<void>(url, {
			metadata: {
				active: dashboard.metadata.actif,
				[camelToSnake('shortLabel')]: dashboard.metadata.label,
			},
		});
	}

	public deleteDashboard(dashboardId: number): Observable<void> {
		const url = this.urlService.generateUrl(
			this.resource,
			{},
			{
				project: this.projectId,
				ids: dashboardId,
			}
		);
		return this.http.delete<void>(url);
	}

	public deleteMany(dashboardIds: Array<number>): Observable<void> {
		const url = this.urlService.generateUrl(
			this.resource,
			{},
			{
				project: this.projectId,
				ids: dashboardIds,
			}
		);
		return this.http.delete<void>(url);
	}
}
